import { z } from 'zod';
import { SOLUTION_STATUSES } from '../enums.js';
import { optionalText, paginationQuerySchema, toPatchSchema, uuidSchema } from './common.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * The knowledge base. Zoho's Solutions module: a question, an answer, and a
 * review state before it is visible to anyone outside the team.
 */
export const createSolutionSchema = z.object({
  title: z.string().trim().min(1, 'Required').max(300),
  status: z.enum(SOLUTION_STATUSES).default('DRAFT'),
  question: optionalText(8000),
  answer: optionalText(16_000),
  comments: optionalText(4000),
  /**
   * Visible beyond the team. Separate from `status` on purpose: an approved
   * answer is not automatically one you want customers reading.
   */
  published: z.coerce.boolean().default(false),

  productId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateSolutionInput = z.infer<typeof createSolutionSchema>;

export const updateSolutionSchema = toPatchSchema(createSolutionSchema);
export type UpdateSolutionInput = z.infer<typeof updateSolutionSchema>;

export const SOLUTION_SORT_FIELDS = ['createdAt', 'updatedAt', 'title', 'status'] as const;

export const listSolutionsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(SOLUTION_SORT_FIELDS).default('updatedAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  status: z.enum(SOLUTION_STATUSES).optional(),
  productId: uuidSchema.optional(),
  publishedOnly: z.stringbool().optional(),
});
export type ListSolutionsQuery = z.infer<typeof listSolutionsQuerySchema>;

export interface SolutionSummary {
  id: string;
  solutionNumber: string;
  title: string;
  status: (typeof SOLUTION_STATUSES)[number];
  published: boolean;
  product: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface SolutionDetail extends SolutionSummary {
  question: string | null;
  answer: string | null;
  comments: string | null;
  customFields: Record<string, unknown>;
}
